import {
  action,
  autorun,
  makeObservable,
  observable,
  type IReactionDisposer,
} from 'mobx'
import debounce from 'lodash/debounce'
import type { OrderDraft } from 'components/Account/types/OrderDraft.types'
import { logger } from 'services/logger'
import { serializeBookWithPhotos } from 'services/PhotoBook/photoBookDTO'
import type { SerializedPhotoBook } from 'services/PhotoBook/photoBookDTO.types'
import type { StoredPhotoBook } from 'services/PhotoBook/photoBookSaver.types'
import { saveBooksToUserCart } from 'services/UserCart/bookUserCart'
import { deepToJS } from 'utils/mobx/deepToJS'
import {
  readMobxSnapshot,
  readOutsideReaction,
} from 'utils/mobx/readOutsideReaction'
import { isPhotoCover } from 'components/PhotoBook/PhotoBookProvider/PhotoBook.utils'
import type { PhotoBankStore } from '../photoBankStore/PhotoBankStore'

export const REMOTE_SYNC_DELAY_MS = 3000

type OrderDraftBooksWrite = Pick<OrderDraft, 'photoBooks' | 'bookIdsToDelete'>

type DraftUpsertWaiter = {
  resolve: (result: unknown) => void
  reject: (error: unknown) => void
}

type PendingDraftBookUpsert = {
  serializedBook: SerializedPhotoBook
  waiters: DraftUpsertWaiter[]
}

export type OrderDraftSaver = (
  orderDraft: OrderDraftBooksWrite
) => Promise<unknown> | void

/**
 * Pushes the books to the two places they live outside this browser: the
 * user cart, which drives the abandoned-cart email, and the account's
 * order draft, which restores them on another device.
 *
 * The user cart is one slot replaced wholesale, so every push carries all
 * the books whose photos have landed. Each book independently decides when it
 * is ready for the order draft; this class coalesces books that become ready
 * together into one upsert, supplies the write-ordering queue, the saver and
 * the account gate, and takes deletions by book id.
 */
export class PhotoBooksRemoteSyncer {
  private readonly photoBankStore: PhotoBankStore

  private readonly readers: {
    getBooksToSync: () => StoredPhotoBook[]
    getIsAnyBookWithheldFromSync: () => boolean
    getIsAccountSyncReady: () => boolean
  }

  private disposers: IReactionDisposer[] = []

  private pendingRemoteWrites: Promise<unknown> = Promise.resolve()

  private pendingDraftBookUpserts = new Map<string, PendingDraftBookUpsert>()

  @observable.ref private accessor orderDraftSaver: OrderDraftSaver | null =
    null

  constructor(
    photoBankStore: PhotoBankStore,
    readers: {
      getBooksToSync: () => StoredPhotoBook[]
      getIsAnyBookWithheldFromSync: () => boolean
      getIsAccountSyncReady: () => boolean
    }
  ) {
    this.photoBankStore = photoBankStore
    this.readers = readers
    makeObservable(this)
  }

  @action
  setOrderDraftSaver(orderDraftSaver: OrderDraftSaver | null): void {
    if (this.orderDraftSaver !== orderDraftSaver) {
      this.cancelPendingDraftUpserts()
    }
    this.orderDraftSaver = orderDraftSaver
  }

  // A trailing debounce, not the autorun's own `delay`: MobX's delay is a
  // throttle that re-arms after every run, which would push the books to the
  // network every few seconds for as long as the user keeps editing. The
  // debounce pushes once, after the burst ends.
  private readonly debouncedPushBooks = debounce((books: StoredPhotoBook[]) => {
    // The gate is re-checked against the current bank: the staged books are
    // snapshots, and a photo removed from the bank since they were staged
    // would otherwise serialize as photo: null in the pushed payload.
    const remotePhotoUids = this.readRemotePhotoUids()
    const pushableBooks = books.filter((book) =>
      areAllBookPhotosUploaded(book, (uid) => remotePhotoUids.has(uid))
    )
    if (pushableBooks.length === 0) {
      return
    }

    this.pushBooks(pushableBooks)
  }, REMOTE_SYNC_DELAY_MS)

  private readonly debouncedFlushDraftBookUpserts = debounce(() => {
    this.flushDraftBookUpserts()
  }, 0)

  startSyncing(): void {
    this.disposers.push(
      autorun(
        () => {
          if (this.readers.getIsAnyBookWithheldFromSync()) {
            // One book being withheld holds the whole push: the slot this
            // writes is replaced wholesale, so pushing the rest would erase
            // that book remotely. Releasing it re-fires this.
            this.debouncedPushBooks.cancel()
            return
          }

          const liveBooks = this.readers.getBooksToSync()
          if (liveBooks.length === 0) {
            // Every book is gone or frozen — a push staged from the previous
            // state must not land after an explicit delete, and the reactive
            // channel never writes emptiness (only a deletion does).
            this.debouncedPushBooks.cancel()
            return
          }

          // Snapshotting subscribes this autorun to every leaf of every book,
          // and keeps observable objects out of the debounced save.
          const books = liveBooks.map((book) => deepToJS(book))
          const remotePhotoUids = new Set(
            this.photoBankStore.remotePhotos.map((photo) => photo?.uid)
          )
          if (
            !books.some((book) =>
              areAllBookPhotosUploaded(book, (uid) => remotePhotoUids.has(uid))
            )
          ) {
            // Every book has photos still uploading; this re-fires the moment
            // the bank settles.
            this.debouncedPushBooks.cancel()
            return
          }

          this.debouncedPushBooks(books)
        },
        {
          name: 'PhotoBooksRemoteSyncer:syncBooks',
          delay: 150,
        }
      )
    )
  }

  // A deletion reaches both channels now, not after a debounce. The user cart
  // gets the pushable survivors — the one push allowed to carry
  // emptiness. The draft is only told which book went: its other books are
  // already there, and holding the deletion for the bank or the account gate
  // would let the next restore bring the book back.
  pushBooksAfterDelete(deletedBookId: string): void {
    this.debouncedPushBooks.cancel()
    this.cancelPendingDraftUpsert(deletedBookId)
    const survivingBooks = readMobxSnapshot(() => this.readers.getBooksToSync())
    const remotePhotoUids = this.readRemotePhotoUids()
    const serializedBooks = survivingBooks
      .filter((book) =>
        areAllBookPhotosUploaded(book, (uid) => remotePhotoUids.has(uid))
      )
      .map((book) => this.serializeBookWithItsPhotos(book))

    this.enqueueRemoteWrite(() => saveBooksToUserCart(serializedBooks))
    this.saveOrderDraft({ bookIdsToDelete: [deletedBookId] })
  }

  isBookPushable(book: StoredPhotoBook): boolean {
    return areAllBookPhotosUploaded(book, (uid) =>
      this.photoBankStore.remotePhotosMap.has(uid)
    )
  }

  pushBookToDraft(book: StoredPhotoBook): Promise<unknown> | null {
    const orderDraftSaver = readOutsideReaction(() => this.orderDraftSaver)
    if (!orderDraftSaver) return null

    const serializedBook = this.serializeBookWithItsPhotos(book)
    return new Promise((resolve, reject) => {
      const pendingUpsert = this.pendingDraftBookUpserts.get(book.bookId)
      if (pendingUpsert) {
        pendingUpsert.serializedBook = serializedBook
        pendingUpsert.waiters.push({ resolve, reject })
      } else {
        this.pendingDraftBookUpserts.set(book.bookId, {
          serializedBook,
          waiters: [{ resolve, reject }],
        })
      }

      this.debouncedFlushDraftBookUpserts()
    })
  }

  cancelPendingDraftUpserts(): void {
    this.debouncedFlushDraftBookUpserts.cancel()
    const pendingUpserts = this.pendingDraftBookUpserts
    this.pendingDraftBookUpserts = new Map()
    this.rejectDraftBookUpserts(
      pendingUpserts,
      new Error('Photo book draft upserts canceled')
    )
  }

  dispose(): void {
    this.debouncedPushBooks.cancel()
    this.cancelPendingDraftUpserts()
    this.disposers.forEach((dispose) => dispose())
    this.disposers = []
  }

  private pushBooks(books: StoredPhotoBook[]): void {
    const serializedBooks = books.map((book) =>
      this.serializeBookWithItsPhotos(book)
    )
    this.enqueueRemoteWrite(() => saveBooksToUserCart(serializedBooks))
  }

  private flushDraftBookUpserts(): void {
    const pendingUpserts = this.pendingDraftBookUpserts
    this.pendingDraftBookUpserts = new Map()
    if (pendingUpserts.size === 0) return

    const orderDraftSaver = readOutsideReaction(() => this.orderDraftSaver)
    if (!orderDraftSaver) {
      this.rejectDraftBookUpserts(
        pendingUpserts,
        new Error('Photo book draft saver unavailable')
      )
      return
    }

    const photoBooks = Object.fromEntries(
      Array.from(pendingUpserts, ([bookId, pendingUpsert]) => [
        bookId,
        pendingUpsert.serializedBook,
      ])
    )
    this.enqueueRemoteWrite(() => orderDraftSaver({ photoBooks })).then(
      (result) => {
        for (const pendingUpsert of pendingUpserts.values()) {
          pendingUpsert.waiters.forEach(({ resolve }) => resolve(result))
        }
      },
      (error: unknown) => this.rejectDraftBookUpserts(pendingUpserts, error)
    )
  }

  private cancelPendingDraftUpsert(bookId: string): void {
    const pendingUpsert = this.pendingDraftBookUpserts.get(bookId)
    if (!pendingUpsert) return

    this.pendingDraftBookUpserts.delete(bookId)
    pendingUpsert.waiters.forEach(({ reject }) =>
      reject(new Error('Photo book draft upsert canceled'))
    )
    if (this.pendingDraftBookUpserts.size === 0) {
      this.debouncedFlushDraftBookUpserts.cancel()
    }
  }

  private rejectDraftBookUpserts(
    pendingUpserts: Map<string, PendingDraftBookUpsert>,
    error: unknown
  ): void {
    for (const pendingUpsert of pendingUpserts.values()) {
      pendingUpsert.waiters.forEach(({ reject }) => reject(error))
    }
  }

  private serializeBookWithItsPhotos(
    book: StoredPhotoBook
  ): SerializedPhotoBook {
    return serializeBookWithPhotos(
      book,
      readOutsideReaction(() => this.photoBankStore.remotePhotos)
    )
  }

  private readRemotePhotoUids(): Set<string | undefined> {
    return readOutsideReaction(
      () => new Set(this.photoBankStore.remotePhotos.map((photo) => photo?.uid))
    )
  }

  isDraftPushReady(): boolean {
    return this.readers.getIsAccountSyncReady() && this.orderDraftSaver !== null
  }

  private saveOrderDraft(orderDraft: OrderDraftBooksWrite): void {
    const orderDraftSaver = readOutsideReaction(() => this.orderDraftSaver)
    if (!orderDraftSaver) return

    this.enqueueRemoteWrite(() => orderDraftSaver(orderDraft))
  }

  // Remote writes run strictly in the order they were issued: a delete fired
  // while a debounced save is still in flight must land after it, or the
  // deleted book comes back on the next restore.
  private enqueueRemoteWrite(
    write: () => Promise<unknown> | void
  ): Promise<unknown> {
    const result = this.pendingRemoteWrites.then(write)
    this.pendingRemoteWrites = result.catch((err) => {
      logger.error('Failed to sync photo books remotely', err)
    })
    return result
  }
}

// A cover photo still uploading matters as much as a page's: a push without it
// serializes `photo: null`, and a draft restored from that payload reads back
// as no book at all.
//
// A book with no pages has nothing left to wait for — the state a book sits in
// between its cover and its first page, and where a one-photo book stays. It
// carries the same cart row as any other book, so it belongs in both remote
// channels too.
function areAllBookPhotosUploaded(
  book: StoredPhotoBook,
  hasRemotePhoto: (uid: string) => boolean
): boolean {
  if (isPhotoCover(book.cover) && !hasRemotePhoto(book.cover.photo.uid)) {
    return false
  }

  const { pages } = book
  return pages.every(
    (page, index) =>
      index === pages.length - 1 || // last page might be empty
      (page.front.photos.length > 0 &&
        page.back.photos.length > 0 &&
        hasRemotePhoto(page.front.photos[0].uid) &&
        hasRemotePhoto(page.back.photos[0].uid))
  )
}
